// The browser phone: one Twilio Device per signed-in tab, one call at a time (SPEC §15.1, §7.1).
// API verified against node_modules/@twilio/voice-sdk v2.18 (es5/twilio/device.d.ts, call.d.ts, audiohelper.d.ts):
//   new Device(token, { codecPreferences, edge, closeProtection }) · register() · updateToken() · connect({ params })
//   Device events: registering, registered, unregistered, error(err, call?), incoming(call), tokenWillExpire
//   Call events: ringing(hasEarlyMedia), accept, disconnect, cancel, reject, error, mute(isMuted), warning(name),
//                warning-cleared(name), reconnecting, reconnected · call.mute(), sendDigits(), status()
//   device.audio: availableInputDevices / availableOutputDevices (Map), setInputDevice(id), speakerDevices.set(id),
//                 ringtoneDevices.set(id), isOutputSelectionSupported, 'deviceChange' event
// The SDK (~350 kB minified) is imported lazily on the first start(), so sign-in and first paint don't wait for it;
// only its types are imported statically.
import type { Call, Device } from '@twilio/voice-sdk';
import { ApiFailure, errorMessage } from '../api';
import { toast } from '../components/ui';
import { endpoints } from '../dialer/endpoints';
import { INBOUND_CALL_PARAMS } from '../dialer/types';
import { explainPhoneError, explainWarning, inboundEndedMessage, voiceTokenProblem, type PhoneErrorLike } from './errors';

type VoiceSdk = typeof import('@twilio/voice-sdk');
let sdkLoad: Promise<VoiceSdk> | null = null;

/** Loads @twilio/voice-sdk once (its own chunk). A failed load is forgotten so Retry fetches it again. */
function loadSdk(): Promise<VoiceSdk> {
  if (!sdkLoad) sdkLoad = import('@twilio/voice-sdk').catch((e) => { sdkLoad = null; throw e; });
  return sdkLoad;
}

/** Call.State.Closed without importing the SDK at runtime. */
export function callClosed(call: Call): boolean {
  return (call.status() as string) === 'closed';
}

export type PhoneStatus =
  | 'off'             // not started (signed out)
  | 'starting'        // fetching a token / creating the Device
  | 'registering'     // connecting to Twilio for incoming calls
  | 'ready'           // registered: can call out and receive calls
  | 'offline'         // lost the Twilio connection; the SDK is reconnecting
  | 'error'           // needs attention (message says what)
  | 'unsupported'     // this browser can't do WebRTC calling
  | 'not_configured'; // the server has no Twilio credentials yet

export type MicState = 'unknown' | 'prompt' | 'granted' | 'denied';

export interface AudioOption { id: string; label: string }

export interface ActiveCallInfo {
  direction: 'outbound' | 'inbound';
  startedAt: number;
  connectedAt: number | null;
  muted: boolean;
  reconnecting: boolean;
  from: string | null;          // inbound: caller's number
  businessId: number | null;    // inbound: matched business (TwiML <Parameter name="businessId">)
  inboundCallId: number | null; // inbound: our calls row (TwiML <Parameter name="inboundCallId">)
  businessName: string | null;  // inbound: TwiML <Parameter name="businessName">
}

export interface IncomingInfo { from: string; businessId: number | null; businessName: string | null; inboundCallId: number | null; receivedAt: number }

export interface PhoneState {
  status: PhoneStatus;
  message: string | null;       // why the phone isn't ready, in one sentence (setter-facing)
  detail: string | null;        // the server's technical reason (e.g. which secrets are missing): shown to admins only
  mic: MicState;
  warnings: string[];           // active call-quality warnings (plain English)
  inputs: AudioOption[];
  outputs: AudioOption[];
  inputId: string | null;
  outputId: string | null;
  outputSelectable: boolean;
  active: ActiveCallInfo | null;
  incoming: IncomingInfo | null;
}

const INITIAL: PhoneState = {
  status: 'off', message: null, detail: null, mic: 'unknown', warnings: [], inputs: [], outputs: [], inputId: null, outputId: null,
  outputSelectable: false, active: null, incoming: null,
};

const PREF_INPUT = 'vd.audio.input';
const PREF_OUTPUT = 'vd.audio.output';

function readPref(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writePref(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode: preference just isn't remembered */ }
}

function numParam(v: string | undefined): number | null {
  const n = Number(v);
  return v && Number.isInteger(n) && n > 0 ? n : null;
}

export class PhoneManager {
  private device: Device | null = null;
  private call: Call | null = null;          // the single active call (outbound, or an accepted inbound)
  private incomingCall: Call | null = null;  // ringing inbound call awaiting Accept/Decline
  private state: PhoneState = INITIAL;
  private listeners = new Set<() => void>();
  private generation = 0;                    // bumps on stop(): late async work from an old session is dropped
  private starting: Promise<void> | null = null;
  private connecting = false;                // device.connect() in flight
  private cancelWhileConnecting = false;     // hang-up pressed before connect() resolved
  private tokenRetry: ReturnType<typeof setTimeout> | null = null;
  private warningNames = new Set<string>();
  private stopAfterCall: (() => void) | null = null; // pending stopWhenIdle() subscription

  getState(): PhoneState { return this.state; }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private set(patch: Partial<PhoneState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** Create and register the Device. Safe to call repeatedly. */
  start(): Promise<void> {
    this.stopAfterCall?.();
    this.stopAfterCall = null;
    if (this.device) return Promise.resolve();
    if (!this.starting) this.starting = this.doStart().finally(() => { this.starting = null; });
    return this.starting;
  }

  private async doStart(): Promise<void> {
    const gen = this.generation;
    this.set({ status: 'starting', message: null, detail: null });
    let sdk: VoiceSdk;
    try {
      sdk = await loadSdk();
    } catch (e) {
      if (gen !== this.generation) return;
      this.set({ status: 'error', message: `Couldn't load the phone software: ${errorMessage(e)}. Check your internet, then click Retry.` });
      return;
    }
    if (gen !== this.generation) return;
    if (!sdk.Device.isSupported) {
      this.set({ status: 'unsupported', message: "This browser can't make calls. Use the latest Chrome or Edge on a computer." });
      return;
    }
    void this.watchMicPermission();
    let token: string;
    try {
      token = (await endpoints.voiceToken()).token;
    } catch (e) {
      if (gen !== this.generation) return;
      const p = e instanceof ApiFailure
        ? voiceTokenProblem(e.status, e.body.error, e.body.message)
        : voiceTokenProblem(null, null, errorMessage(e));
      this.set({ status: p.status, message: p.message, detail: p.detail });
      return;
    }
    if (gen !== this.generation) return;

    let device: Device;
    try {
      device = new sdk.Device(token, {
        codecPreferences: [sdk.Call.Codec.Opus, sdk.Call.Codec.PCMU],
        edge: 'ashburn',
        closeProtection: true, // the browser asks before closing the tab mid-call
      });
    } catch (e) {
      this.set({ status: 'error', message: `The phone couldn't start: ${errorMessage(e)}` });
      return;
    }
    this.device = device;

    device.on('registering', () => { if (this.device === device) this.set({ status: 'registering', message: null }); });
    device.on('registered', () => { if (this.device === device) this.set({ status: 'ready', message: null }); });
    device.on('unregistered', () => {
      if (this.device === device && this.state.status !== 'error') {
        this.set({ status: 'offline', message: "Phone disconnected from Twilio; incoming calls won't ring until it reconnects." });
      }
    });
    device.on('tokenWillExpire', () => { void this.refreshToken(); });
    device.on('error', (err: PhoneErrorLike, call?: Call) => this.onDeviceError(device, err, call));
    device.on('incoming', (call: Call) => this.onIncoming(call));

    const audio = device.audio;
    if (audio) {
      audio.on('deviceChange', () => this.refreshAudio());
      this.refreshAudio();
    }

    try {
      await device.register();
    } catch (e) {
      if (this.device !== device) return;
      const plain = explainPhoneError(e as PhoneErrorLike);
      this.set({ status: plain.kind === 'network' ? 'offline' : 'error', message: plain.message });
    }
    if (this.state.mic === 'granted') await this.applyStoredAudio();
  }

  /**
   * Signed out (or the session expired). A live call is never cut: the Device is destroyed when that call ends.
   * A ringing inbound call is declined so it falls through to the voicemail box.
   */
  stopWhenIdle(): void {
    if (this.incomingCall) this.rejectIncoming();
    if (!this.call && !this.connecting) { this.stop(); return; }
    this.stopAfterCall?.();
    const unsub = this.subscribe(() => {
      if (!this.state.active && !this.connecting) { unsub(); this.stopAfterCall = null; this.stop(); }
    });
    this.stopAfterCall = unsub;
  }

  /** Signed out: tear everything down. */
  stop(): void {
    this.generation++;
    if (this.tokenRetry) clearTimeout(this.tokenRetry);
    this.tokenRetry = null;
    try { this.device?.destroy(); } catch { /* already gone */ }
    this.device = null;
    this.call = null;
    this.incomingCall = null;
    this.warningNames.clear();
    this.state = { ...INITIAL };
    for (const fn of this.listeners) fn();
  }

  /** "Retry" in the status pill. Never interrupts a live call. */
  async restart(): Promise<void> {
    if (this.call || this.connecting) return;
    const mic = this.state.mic;
    this.stop();
    this.state = { ...this.state, mic };
    await this.start();
  }

  private async refreshToken(attempt = 0): Promise<void> {
    const device = this.device;
    if (!device) return;
    try {
      const { token } = await endpoints.voiceToken();
      if (this.device === device) device.updateToken(token);
    } catch (e) {
      if (this.device !== device) return;
      if (attempt >= 4) {
        this.set({ status: 'error', message: `Couldn't renew the phone login: ${errorMessage(e)}. Reload the page.` });
        return;
      }
      this.tokenRetry = setTimeout(() => { void this.refreshToken(attempt + 1); }, 5000 * (attempt + 1));
    }
  }

  private onDeviceError(device: Device, err: PhoneErrorLike, call?: Call): void {
    if (this.device !== device) return;
    const plain = explainPhoneError(err);
    if (plain.kind === 'token' && (plain.code === 20104 || plain.code === 31205)) { void this.refreshToken(); return; }
    if (plain.kind === 'mic') this.set({ mic: plain.code === 31401 ? 'denied' : this.state.mic });
    if (call) return; // call-scoped errors are reported by whoever owns the call (the dialer or the call bar)
    if (plain.kind === 'network') this.set({ status: 'offline', message: plain.message });
    else if (plain.kind === 'token' || plain.kind === 'auth') this.set({ status: 'error', message: plain.message });
    else toast(plain.message, 'bad');
  }

  // ---------------------------------------------------------------- microphone and audio devices

  private async watchMicPermission(): Promise<void> {
    try {
      const st = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      this.set({ mic: st.state as MicState });
      st.onchange = () => {
        this.set({ mic: st.state as MicState });
        if (st.state === 'granted') void this.afterMicGranted();
      };
    } catch {
      this.set({ mic: 'unknown' }); // Firefox/Safari can't query the microphone permission; the first call will ask
    }
  }

  /** Asks for the microphone ahead of the first dial ("Test microphone"). */
  async requestMic(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      this.set({ mic: 'granted' });
      await this.afterMicGranted();
      return true;
    } catch (e) {
      const name = (e as { name?: string }).name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.set({ mic: 'denied' });
        toast(explainPhoneError({ code: 31401 }).message, 'bad');
      } else if (name === 'NotFoundError') {
        toast('No microphone found. Plug in your headset and try again.', 'bad');
      } else {
        toast(explainPhoneError({ code: 31402 }).message, 'bad');
      }
      return false;
    }
  }

  private async afterMicGranted(): Promise<void> {
    try { await this.device?.audio?._updateAvailableDevices(); } catch { /* list refreshes on the next deviceChange */ }
    this.refreshAudio();
    await this.applyStoredAudio();
  }

  private refreshAudio(): void {
    const a = this.device?.audio;
    if (!a) return;
    const label = (d: MediaDeviceInfo, kind: string) => d.label || (d.deviceId === 'default' ? 'System default' : `${kind} (allow the microphone to see names)`);
    const outputs = [...a.availableOutputDevices.values()].map((d) => ({ id: d.deviceId, label: label(d, 'Speaker') }));
    const speaker = [...a.speakerDevices.get()][0];
    this.set({
      inputs: [...a.availableInputDevices.values()].map((d) => ({ id: d.deviceId, label: label(d, 'Microphone') })),
      outputs,
      inputId: a.inputDevice?.deviceId ?? readPref(PREF_INPUT),
      outputId: speaker?.deviceId ?? readPref(PREF_OUTPUT),
      outputSelectable: a.isOutputSelectionSupported,
    });
  }

  private async applyStoredAudio(): Promise<void> {
    const a = this.device?.audio;
    if (!a) return;
    const input = readPref(PREF_INPUT);
    const output = readPref(PREF_OUTPUT);
    try {
      if (input && a.availableInputDevices.has(input) && a.inputDevice?.deviceId !== input) await a.setInputDevice(input);
      if (output && a.isOutputSelectionSupported && a.availableOutputDevices.has(output)) {
        await a.speakerDevices.set(output);
        await a.ringtoneDevices.set(output);
      }
    } catch { /* a remembered device was unplugged; the system default stays */ }
    this.refreshAudio();
  }

  async setInput(id: string): Promise<void> {
    const a = this.device?.audio;
    if (!a) return;
    try {
      await a.setInputDevice(id);
      writePref(PREF_INPUT, id);
    } catch (e) {
      toast(`Couldn't switch microphone: ${errorMessage(e)}`, 'bad');
    }
    this.refreshAudio();
  }

  async setOutput(id: string): Promise<void> {
    const a = this.device?.audio;
    if (!a || !a.isOutputSelectionSupported) return;
    try {
      await a.speakerDevices.set(id);
      await a.ringtoneDevices.set(id);
      writePref(PREF_OUTPUT, id);
    } catch (e) {
      toast(`Couldn't switch speaker: ${errorMessage(e)}`, 'bad');
    }
    this.refreshAudio();
  }

  /** Plays Twilio's test tone through the chosen speaker. */
  async testSpeaker(): Promise<void> {
    try { await this.device?.audio?.speakerDevices.test(); } catch (e) { toast(`Speaker test failed: ${errorMessage(e)}`, 'bad'); }
  }

  // ---------------------------------------------------------------- calls

  /** Why a call can't start right now, or null when it can. */
  whyCantCall(): string | null {
    const s = this.state;
    if (s.status === 'unsupported' || s.status === 'not_configured') return s.message;
    if (!this.device) return s.status === 'starting' ? 'The phone is still starting; try again in a second.' : (s.message ?? "The phone isn't connected. Click the phone status to retry.");
    if (s.status === 'error') return s.message ?? 'The phone has an error. Click the phone status to retry.';
    if (s.mic === 'denied') return explainPhoneError({ code: 31401 }).message;
    if (this.call || this.connecting) return "You're already on a call.";
    if (this.incomingCall) return 'Answer or decline the incoming call first.';
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return "You're offline. Check your internet connection.";
    return null;
  }

  hasCall(): boolean { return this.call !== null || this.connecting; }

  /**
   * Starts an outbound call through the TwiML App (/twilio/voice). The caller attaches its own listeners to the
   * returned Call; this manager tracks mute/warnings/end for the status pill and the call bar.
   */
  async connect(params: Record<string, string>): Promise<Call> {
    const why = this.whyCantCall();
    if (why) throw new Error(why);
    const device = this.device!;
    this.connecting = true;
    this.cancelWhileConnecting = false;
    let call: Call;
    try {
      call = await device.connect({ params });
    } catch (e) {
      throw new Error(explainPhoneError(e as PhoneErrorLike).kind === 'other' ? `The call couldn't start: ${errorMessage(e)}` : explainPhoneError(e as PhoneErrorLike).message);
    } finally {
      this.connecting = false;
    }
    this.attach(call, { direction: 'outbound', from: null, businessId: null, businessName: null, inboundCallId: null });
    if (this.cancelWhileConnecting) call.disconnect();
    return call;
  }

  private attach(call: Call, info: Pick<ActiveCallInfo, 'direction' | 'from' | 'businessId' | 'businessName' | 'inboundCallId'>): void {
    this.call = call;
    this.warningNames.clear();
    this.set({ active: { ...info, startedAt: Date.now(), connectedAt: null, muted: false, reconnecting: false }, warnings: [] });
    const mine = () => this.call === call;
    const patchActive = (p: Partial<ActiveCallInfo>) => { if (mine() && this.state.active) this.set({ active: { ...this.state.active, ...p } }); };
    const end = () => {
      if (!mine()) return;
      this.call = null;
      this.warningNames.clear();
      this.set({ active: null, warnings: [] });
    };
    call.on('accept', () => patchActive({ connectedAt: Date.now() }));
    call.on('mute', (isMuted: boolean) => patchActive({ muted: isMuted }));
    call.on('reconnecting', () => patchActive({ reconnecting: true }));
    call.on('reconnected', () => patchActive({ reconnecting: false }));
    call.on('warning', (name: string) => {
      if (!mine()) return;
      this.warningNames.add(name);
      this.set({ warnings: [...this.warningNames].map(explainWarning) });
    });
    call.on('warning-cleared', (name: string) => {
      if (!mine()) return;
      this.warningNames.delete(name);
      this.set({ warnings: [...this.warningNames].map(explainWarning) });
    });
    call.on('disconnect', end);
    call.on('cancel', end);
    call.on('reject', end);
    call.on('error', () => { if (callClosed(call)) end(); });
  }

  /** Hang up the active call (or the one still connecting). */
  hangup(): void {
    if (this.call) { this.call.disconnect(); return; }
    if (this.connecting) this.cancelWhileConnecting = true;
  }

  /** Last resort when a hang-up never confirmed: drop every call this Device holds and forget the active one. */
  forceEnd(): void {
    try { this.device?.disconnectAll(); } catch { /* nothing left to disconnect */ }
    if (this.call) {
      this.call = null;
      this.warningNames.clear();
      this.set({ active: null, warnings: [] });
    }
  }

  toggleMute(): boolean {
    if (!this.call) return false;
    const next = !this.call.isMuted();
    this.call.mute(next);
    return next;
  }

  sendDigits(digits: string): void {
    if (!this.call || !/^[0-9*#w]+$/.test(digits)) return;
    this.call.sendDigits(digits);
  }

  // ---------------------------------------------------------------- inbound

  private onIncoming(call: Call): void {
    if (this.call || this.incomingCall || this.connecting) { call.reject(); return; } // one call at a time
    this.incomingCall = call;
    const from = call.parameters?.From || 'Unknown number';
    const info: IncomingInfo = {
      from,
      businessId: numParam(call.customParameters.get(INBOUND_CALL_PARAMS.businessId)),
      businessName: call.customParameters.get(INBOUND_CALL_PARAMS.businessName) || null,
      inboundCallId: numParam(call.customParameters.get(INBOUND_CALL_PARAMS.callId)),
      receivedAt: Date.now(),
    };
    this.set({ incoming: info });
    const clear = () => {
      if (this.incomingCall !== call) return;
      this.incomingCall = null;
      this.set({ incoming: null });
    };
    call.on('cancel', () => {
      // 'cancel' = the invite was withdrawn: the caller hung up, or a teammate in the ring group answered first.
      if (this.incomingCall === call) toast(inboundEndedMessage(from), 'info');
      clear();
    });
    call.on('reject', clear);
    call.on('disconnect', clear);
    call.on('error', () => { if (callClosed(call)) clear(); });
  }

  acceptIncoming(): void {
    const call = this.incomingCall;
    if (!call) return;
    this.incomingCall = null;
    const inc = this.state.incoming;
    this.set({ incoming: null });
    if (this.call) { call.reject(); toast('Declined: you were already on a call.', 'warn'); return; }
    this.attach(call, { direction: 'inbound', from: inc?.from ?? null, businessId: inc?.businessId ?? null, businessName: inc?.businessName ?? null, inboundCallId: inc?.inboundCallId ?? null });
    call.on('error', (err: PhoneErrorLike) => toast(explainPhoneError(err).message, 'bad'));
    call.accept();
  }

  rejectIncoming(): void {
    const call = this.incomingCall;
    if (!call) return;
    this.incomingCall = null;
    this.set({ incoming: null });
    call.reject();
  }
}

/** The app's single phone. Started by <PhoneProvider> after sign-in, stopped on sign-out. */
export const phone = new PhoneManager();
